import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../features/auth/AuthContext';
import { usePageTitle } from '../lib/usePageTitle';
import { formatDateTimeFull, formatPoints } from '../lib/dates';
import { AppLayout } from '../components/AppLayout';
import { api } from '../lib/api';

interface DashboardCard {
  league: { id: string; name: string; competition: string; myRole: 'ADMIN' | 'PLAYER' };
  nextMatchday: {
    id: string;
    number: number;
    state: string;
    deadline: string | null;
    hasSubmitted: boolean;
  } | null;
  standing: { position: number; totalPoints: number } | null;
  lastMatchday: { number: number; myTotal: number } | null;
}

/**
 * Dashboard (lineeguida §26): per ogni lega la prossima giornata con CTA
 * "FAI I PRONOSTICI", lo stato schedina, posizione e punti.
 */
export function DashboardPage() {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<{ cards: DashboardCard[] }>('/dashboard').then((r) => r.cards),
  });

  return (
    <AppLayout title={`Ciao, ${user?.displayName ?? ''}!`}>
      {dashboard.isLoading && (
        <p role="status" className="mt-6">
          Caricamento delle tue leghe…
        </p>
      )}

      {dashboard.isError && (
        <p role="alert" className="mt-6 font-medium text-danger">
          Non riesco a caricare la dashboard. Riprova più tardi.
        </p>
      )}

      {dashboard.data && dashboard.data.length === 0 && (
        <div className="mt-8 rounded-2xl border border-ink-200 p-6 dark:border-ink-800">
          <p className="text-lg">Non fai ancora parte di nessuna lega.</p>
          <p className="mt-2 text-ink-700 dark:text-ink-200">
            Crea la tua lega e invita gli amici, oppure entra con un codice invito.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/leghe/nuova"
              className="button inline-flex items-center justify-center rounded-xl bg-panda-700 px-6 py-2.5 font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600"
            >
              Crea una lega
            </Link>
            <Link
              to="/entra-con-codice"
              className="button inline-flex items-center justify-center rounded-xl border-2 border-panda-700 px-6 py-2.5 font-semibold text-panda-700 no-underline hover:bg-panda-50 dark:border-panda-400 dark:text-panda-300"
            >
              Ho un codice invito
            </Link>
          </div>
        </div>
      )}

      {dashboard.data && dashboard.data.length > 0 && (
        <ul className="mt-8 flex flex-col gap-6">
          {dashboard.data.map((card) => (
            <li
              key={card.league.id}
              className="rounded-2xl border border-ink-200 p-5 dark:border-ink-800"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-xl font-bold">
                  <Link to={`/leghe/${card.league.id}`} className="no-underline hover:underline">
                    {card.league.name}
                  </Link>
                </h2>
                <p className="text-ink-600 dark:text-ink-300">
                  {card.league.competition}
                  {card.league.myRole === 'ADMIN' && ' · Sei amministratore'}
                </p>
              </div>

              {/* Posizione e punti */}
              {card.standing && (
                <p className="mt-2 text-lg">
                  Sei <strong>{card.standing.position}º</strong> con{' '}
                  <strong>{formatPoints(card.standing.totalPoints)} punti</strong>
                  {card.lastMatchday &&
                    ` — ultima giornata (${card.lastMatchday.number}ª): ${formatPoints(card.lastMatchday.myTotal)} punti`}
                  .
                </p>
              )}

              {/* Prossima giornata + CTA */}
              {card.nextMatchday ? (
                <div className="mt-4">
                  {card.nextMatchday.state === 'OPEN' ? (
                    <>
                      <p role="status">
                        Giornata {card.nextMatchday.number}:{' '}
                        {card.nextMatchday.hasSubmitted
                          ? 'schedina inviata ✓ (modificabile fino alla chiusura)'
                          : 'non hai ancora fatto i pronostici'}
                        {card.nextMatchday.deadline &&
                          ` — chiude ${formatDateTimeFull(card.nextMatchday.deadline)}`}
                        .
                      </p>
                      <Link
                        to={`/leghe/${card.league.id}/giornate/${card.nextMatchday.id}/pronostici`}
                        className="button mt-3 inline-flex items-center justify-center rounded-xl bg-panda-700 px-8 py-3 text-lg font-semibold text-white no-underline hover:bg-panda-800 dark:bg-panda-600"
                      >
                        {card.nextMatchday.hasSubmitted ? 'Modifica i pronostici' : 'Fai i pronostici'}
                      </Link>
                    </>
                  ) : (
                    <p className="text-ink-700 dark:text-ink-200">
                      Prossima giornata: la {card.nextMatchday.number}ª, in programma
                      {card.nextMatchday.deadline &&
                        ` (chiusura ${formatDateTimeFull(card.nextMatchday.deadline)})`}
                      .
                    </p>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-ink-700 dark:text-ink-200">
                  Nessuna giornata in programma al momento.
                </p>
              )}

              <nav aria-label={`Collegamenti rapidi per ${card.league.name}`} className="mt-4 flex flex-wrap gap-4">
                <Link to={`/leghe/${card.league.id}/classifica`} className="font-medium text-panda-700 underline dark:text-panda-300">
                  Classifica
                </Link>
                <Link to={`/leghe/${card.league.id}/giornate`} className="font-medium text-panda-700 underline dark:text-panda-300">
                  Giornate
                </Link>
                <Link to={`/leghe/${card.league.id}/storico`} className="font-medium text-panda-700 underline dark:text-panda-300">
                  Il mio storico
                </Link>
              </nav>
            </li>
          ))}
        </ul>
      )}

      {dashboard.data && dashboard.data.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/leghe/nuova" className="font-medium text-panda-700 underline dark:text-panda-300">
            Crea un’altra lega
          </Link>
          <Link to="/entra-con-codice" className="font-medium text-panda-700 underline dark:text-panda-300">
            Entra con un codice
          </Link>
        </div>
      )}
    </AppLayout>
  );
}
